import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShoppingCart, ArrowRight, Minus, Plus } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { formatINR } from "@/lib/format";
import { useCart } from "@/lib/cart";
import RatingStars from "@/components/common/RatingStars";

export const QuickView = ({ product, open, onOpenChange }) => {
  const { addItem } = useCart();
  const [variantIdx, setVariantIdx] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setVariantIdx(0);
    setQty(1);
  }, [product]);

  if (!product) return null;
  const variant = product.variants?.[variantIdx] || { label: "Default", price: product.price };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl border-[color:var(--acx-border)] bg-[var(--acx-card)] p-0 text-white">
        <div className="grid grid-cols-1 sm:grid-cols-2">
          <div className="relative aspect-square overflow-hidden bg-black sm:rounded-l-lg">
            <img src={product.images?.[0]} alt={product.name} className="h-full w-full object-cover" />
          </div>
          <div className="flex flex-col p-6">
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/50">{product.type}</span>
            <h3 className="mt-1 font-display text-2xl font-bold tracking-[-0.02em]">{product.name}</h3>
            <div className="mt-2 flex items-center gap-2">
              <RatingStars value={product.rating} size={14} />
              <span className="text-[12px] text-white/50">{product.rating} · {product.reviewCount} reviews</span>
            </div>
            <p className="mt-3 text-[13px] leading-relaxed text-white/65">{product.shortDescription}</p>

            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-2xl font-semibold">{formatINR(variant.price)}</span>
              {variant.mrp > variant.price && (
                <span className="text-sm text-white/40 line-through">{formatINR(variant.mrp)}</span>
              )}
            </div>

            <div className="mt-4">
              <span className="text-[12px] uppercase tracking-[0.16em] text-white/50">Size</span>
              <div className="mt-2 flex flex-wrap gap-2">
                {product.variants?.map((v, i) => (
                  <button
                    key={v.label}
                    onClick={() => setVariantIdx(i)}
                    className={`rounded-[10px] border px-3 py-1.5 text-[13px] transition-colors ${
                      i === variantIdx
                        ? "border-white bg-white text-black"
                        : "border-[color:var(--acx-border)] text-white/80 hover:border-white/30"
                    }`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 flex items-center gap-3">
              <div className="inline-flex items-center rounded-[12px] border border-[color:var(--acx-border)]">
                <button className="px-3 py-2 text-white/70 hover:text-white" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease"><Minus size={15} /></button>
                <span className="w-8 text-center text-sm">{qty}</span>
                <button className="px-3 py-2 text-white/70 hover:text-white" onClick={() => setQty((q) => q + 1)} aria-label="Increase"><Plus size={15} /></button>
              </div>
              <button
                onClick={() => {
                  addItem(product, variant, qty);
                  onOpenChange(false);
                }}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-[12px] bg-white px-4 py-3 text-[14px] font-semibold text-black transition-colors hover:bg-white/90"
              >
                <ShoppingCart size={16} /> Add to Cart
              </button>
            </div>
            <Link
              to={`/product/${product.slug}`}
              onClick={() => onOpenChange(false)}
              className="mt-4 inline-flex items-center gap-1.5 text-[13px] text-white/70 transition-colors hover:text-white"
            >
              View full details <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QuickView;
